import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "../hooks/useAuth";
import { Plus, Loader2, DollarSign, TrendingUp, TrendingDown, Check, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { onlyDigits, formatBRL, centsToBRL } from "@/lib/currency";
import { toast } from "sonner";

interface Transacao {
  id: string;
  tipo: "receita" | "despesa";
  categoria: string | null;
  descricao: string | null;
  valorCentavos: number;
  status: "pendente" | "pago" | "cancelado";
  dataVencimento: string;
  dataPagamento: string | null;
}

const emptyForm = { tipo: "receita", categoria: "", valorDigitos: "", dataVencimento: "", descricao: "" };

export default function Transactions() {
  const { user } = useAuth();
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    if (user) fetchTransacoes();
  }, [user]);

  const fetchTransacoes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/transacoes`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTransacoes(data.transacoes || []);
      } else {
        toast.error("Não foi possível carregar as transações");
      }
    } catch {
      toast.error("Erro de conexão ao carregar transações");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/transacoes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: form.tipo,
          categoria: form.categoria,
          descricao: form.descricao || null,
          valorCentavos: Number(form.valorDigitos || "0"),
          dataVencimento: form.dataVencimento,
        }),
      });
      if (res.ok) {
        toast.success("Transação registrada");
        setShowForm(false);
        setForm({ ...emptyForm });
        fetchTransacoes();
      } else {
        const err = await res.json().catch(() => ({ error: "Erro ao salvar" }));
        toast.error(err.error || "Erro ao salvar transação");
      }
    } catch {
      toast.error("Erro de conexão");
    } finally {
      setSaving(false);
    }
  };

  const marcarPago = async (id: string) => {
    const res = await fetch(`${API_BASE}/api/transacoes/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "pago" }),
    });
    if (res.ok) {
      toast.success("Marcada como paga");
      fetchTransacoes();
    } else {
      toast.error("Não foi possível atualizar");
    }
  };

  const excluir = async (id: string) => {
    const res = await fetch(`${API_BASE}/api/transacoes/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Transação excluída");
      setTransacoes((prev) => prev.filter((t) => t.id !== id));
    } else {
      toast.error("Não foi possível excluir");
    }
  };

  if (!user) return <div className="flex items-center justify-center h-screen">Carregando...</div>;

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const filtradas = transacoes.filter((t) => {
    const okStatus = statusFilter === "todos" || t.status === statusFilter;
    const okTipo = tipoFilter === "todos" || t.tipo === tipoFilter;
    return okStatus && okTipo;
  });

  const totalReceitas = filtradas.filter((t) => t.tipo === "receita").reduce((s, t) => s + t.valorCentavos, 0);
  const totalDespesas = filtradas.filter((t) => t.tipo === "despesa").reduce((s, t) => s + t.valorCentavos, 0);

  const statusLabel: Record<string, string> = { pago: "Pago", pendente: "Pendente", cancelado: "Cancelado" };
  const fmtData = (iso: string) => (iso ? iso.split("-").reverse().join("/") : "-");

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Financeiro</h1>
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Transação
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Receitas</p>
                <p className="text-2xl font-bold text-green-600">{centsToBRL(totalReceitas)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center text-green-600">
                <TrendingUp className="h-6 w-6" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Despesas</p>
                <p className="text-2xl font-bold text-red-600">{centsToBRL(totalDespesas)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
                <TrendingDown className="h-6 w-6" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Saldo</p>
                <p className="text-2xl font-bold">{centsToBRL(totalReceitas - totalDespesas)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <DollarSign className="h-6 w-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4 flex gap-4 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os status</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os tipos</SelectItem>
                <SelectItem value="receita">Receita</SelectItem>
                <SelectItem value="despesa">Despesa</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {showForm && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Nova Transação</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="receita">Receita</SelectItem>
                        <SelectItem value="despesa">Despesa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="categoria">Categoria *</Label>
                    <Input
                      id="categoria"
                      value={form.categoria}
                      onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      placeholder="Ex: Aluguel, Condomínio, IPTU"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="valorTx">Valor (R$) *</Label>
                    <Input
                      id="valorTx"
                      type="text"
                      inputMode="numeric"
                      value={formatBRL(form.valorDigitos)}
                      onChange={(e) => setForm({ ...form, valorDigitos: onlyDigits(e.target.value) })}
                      placeholder="0,00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vencTx">Vencimento *</Label>
                    <Input
                      id="vencTx"
                      type="date"
                      value={form.dataVencimento}
                      onChange={(e) => setForm({ ...form, dataVencimento: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="descTx">Descrição</Label>
                  <Input
                    id="descTx"
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    placeholder="Observações adicionais"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                    Salvar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            {filtradas.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">
                  {statusFilter !== "todos" || tipoFilter !== "todos"
                    ? "Nenhuma transação encontrada com esses filtros."
                    : "Nenhuma transação registrada ainda."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      {["Tipo", "Categoria", "Valor", "Status", "Vencimento", "Descrição", "Ações"].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtradas.map((t) => (
                      <tr key={t.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            t.tipo === "receita" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
                          )}>
                            {t.tipo === "receita" ? "Receita" : "Despesa"}
                          </span>
                        </td>
                        <td className="px-6 py-4">{t.categoria || "-"}</td>
                        <td className="px-6 py-4 font-medium">
                          {t.tipo === "receita" ? "+" : "-"}{centsToBRL(t.valorCentavos)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            t.status === "pago" ? "bg-green-100 text-green-800" :
                            t.status === "pendente" ? "bg-yellow-100 text-yellow-800" :
                            "bg-gray-100 text-gray-800",
                          )}>
                            {statusLabel[t.status]}
                          </span>
                        </td>
                        <td className="px-6 py-4">{fmtData(t.dataVencimento)}</td>
                        <td className="px-6 py-4 text-muted-foreground">{t.descricao || "-"}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            {t.status === "pendente" && (
                              <Button variant="ghost" size="icon" title="Marcar como paga" onClick={() => marcarPago(t.id)}>
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" title="Excluir" onClick={() => excluir(t.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
